import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { authAPI, cartAPI, wishlistAPI } from '../services/api';
import toast from 'react-hot-toast';

// ─── Auth Store ───────────────────────────────────────────────────────────────
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      loading: false,

      setAuth: (user, token) => {
        localStorage.setItem('saree_token', token);
        set({ user, token, isAuthenticated: true });
      },

      login: async (email, password) => {
        set({ loading: true });
        try {
          const { data } = await authAPI.login({ email, password });
          localStorage.setItem('saree_token', data.token);
          set({ user: data.user, token: data.token, isAuthenticated: true, loading: false });
          toast.success(`Welcome back, ${data.user.name.split(' ')[0]}! 🌸`);
          // Sync any guest cart items with backend cart
          await useCartStore.getState().syncCart();
          return { success: true, user: data.user };
        } catch (err) {
          set({ loading: false });
          return { success: false, message: err.response?.data?.message || 'Invalid email or password' };
        }
      },

      register: async (userData) => {
        set({ loading: true });
        try {
          const { data } = await authAPI.register(userData);
          localStorage.setItem('saree_token', data.token);
          set({ user: data.user, token: data.token, isAuthenticated: true, loading: false });
          toast.success('Account created successfully! 🎉');
          // Sync any guest cart items with backend cart
          await useCartStore.getState().syncCart();
          return { success: true, user: data.user };
        } catch (err) {
          set({ loading: false });
          return { success: false, message: err.response?.data?.message || 'Registration failed' };
        }
      },

      logout: async () => {
        try { await authAPI.logout(); } catch {}
        localStorage.removeItem('saree_token');
        localStorage.removeItem('saree_user');
        set({ user: null, token: null, isAuthenticated: false });
        useCartStore.getState().clearCart();
        toast.success('Logged out successfully');
      },

      updateUser: (updates) => set((state) => ({ user: { ...state.user, ...updates } })),

      fetchMe: async () => {
        try {
          const { data } = await authAPI.getMe();
          set({ user: data.user });
        } catch {
          localStorage.removeItem('saree_token');
          localStorage.removeItem('saree_user');
          set({ user: null, token: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: 'saree-auth',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// ─── Cart Store ───────────────────────────────────────────────────────────────
export const useCartStore = create(
  persist(
    subscribeWithSelector((set, get) => ({
      items: [],
      total: 0,
      itemCount: 0,
      loading: false,

      setCart: (cart) => {
        const items = cart?.items || [];
        const total = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
        const itemCount = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
        set({ items, total, itemCount });
      },

      fetchCart: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        set({ loading: true });
        try {
          const { data } = await cartAPI.get();
          get().setCart(data.cart);
        } catch {
        } finally {
          set({ loading: false });
        }
      },

      syncCart: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        const currentItems = get().items || [];
        set({ loading: true });
        try {
          if (currentItems.length > 0) {
            const payload = currentItems.map((i) => ({
              productId: i.product?._id || (typeof i.product === 'string' ? i.product : null),
              quantity: i.quantity || 1,
              color: i.color || '',
            })).filter((i) => i.productId);

            if (payload.length > 0) {
              const { data } = await cartAPI.sync(payload);
              get().setCart(data.cart);
            } else {
              const { data } = await cartAPI.get();
              get().setCart(data.cart);
            }
          } else {
            const { data } = await cartAPI.get();
            get().setCart(data.cart);
          }
        } catch (err) {
          console.error('Failed to sync cart:', err);
          try {
            const { data } = await cartAPI.get();
            get().setCart(data.cart);
          } catch {}
        } finally {
          set({ loading: false });
        }
      },

      addToCart: async (productOrId, quantity = 1, color = '', productObj = null) => {
        const { isAuthenticated } = useAuthStore.getState();
        const productId = typeof productOrId === 'object' && productOrId?._id ? productOrId._id : productOrId;
        const productData = typeof productOrId === 'object' && productOrId?.name ? productOrId : productObj;

        if (isAuthenticated) {
          try {
            const { data } = await cartAPI.add(productId, quantity, color);
            get().setCart(data.cart);
            toast.success('Added to cart! 🛒');
            return true;
          } catch {
            return false;
          }
        }

        // Guest Cart
        const currentItems = [...get().items];
        const existingIdx = currentItems.findIndex(
          (i) => {
            const pId = i.product?._id || (typeof i.product === 'string' ? i.product : null);
            return pId?.toString() === productId?.toString() && (i.color || '') === (color || '');
          }
        );

        const price = productData ? (productData.discountPrice || productData.price || 0) : 0;
        const name = productData?.name || 'Saree';

        if (existingIdx > -1) {
          currentItems[existingIdx] = {
            ...currentItems[existingIdx],
            quantity: Math.min(currentItems[existingIdx].quantity + quantity, 10),
          };
        } else {
          currentItems.push({
            _id: `guest_${productId}_${color || 'default'}_${Date.now()}`,
            product: productData || { _id: productId },
            name,
            color: color || '',
            price,
            quantity: Math.min(quantity, 10),
            addedAt: new Date().toISOString(),
          });
        }

        get().setCart({ items: currentItems });
        toast.success('Added to cart! 🛒');
        return true;
      },

      updateQuantity: async (itemId, quantity) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          try {
            const { data } = await cartAPI.update(itemId, quantity);
            get().setCart(data.cart);
          } catch {}
          return;
        }

        // Guest Cart
        let currentItems = [...get().items];
        if (quantity < 1) {
          currentItems = currentItems.filter((i) => i._id !== itemId);
        } else {
          currentItems = currentItems.map((i) =>
            i._id === itemId ? { ...i, quantity: Math.min(quantity, 10) } : i
          );
        }
        get().setCart({ items: currentItems });
      },

      removeItem: async (itemId) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          try {
            await cartAPI.remove(itemId);
            const items = get().items.filter((i) => i._id !== itemId);
            const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
            const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
            set({ items, total, itemCount });
            toast.success('Item removed from cart');
          } catch {}
          return;
        }

        // Guest Cart
        const items = get().items.filter((i) => i._id !== itemId);
        get().setCart({ items });
        toast.success('Item removed from cart');
      },

      clearCart: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          try {
            await cartAPI.clear();
          } catch {}
        }
        set({ items: [], total: 0, itemCount: 0 });
      },
    })),
    {
      name: 'saree-cart',
      partialize: (state) => ({ items: state.items, total: state.total, itemCount: state.itemCount }),
    }
  )
);

// ─── Wishlist Store ───────────────────────────────────────────────────────────
export const useWishlistStore = create(
  persist(
    (set, get) => ({
      items: [],
      productIds: new Set(),
      loading: false,

      fetchWishlist: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        set({ loading: true });
        try {
          const { data } = await wishlistAPI.get();
          const ids = new Set(data.wishlist.map((p) => p._id));
          set({ items: data.wishlist, productIds: ids });
        } catch {}
        finally { set({ loading: false }); }
      },

      toggle: async (productId) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          toast.error('Please login to save to wishlist');
          return;
        }
        const wasInWishlist = get().productIds.has(productId);
        // Optimistic update
        set((state) => {
          const newIds = new Set(state.productIds);
          if (wasInWishlist) newIds.delete(productId);
          else newIds.add(productId);
          return { productIds: newIds };
        });

        try {
          const { data } = await wishlistAPI.toggle(productId);
          toast.success(data.message);
        } catch {
          // Revert
          set((state) => {
            const newIds = new Set(state.productIds);
            if (wasInWishlist) newIds.add(productId);
            else newIds.delete(productId);
            return { productIds: newIds };
          });
        }
      },

      isWishlisted: (productId) => {
        const ids = get().productIds;
        if (!ids) return false;
        if (ids instanceof Set) return ids.has(productId);
        if (Array.isArray(ids)) return ids.includes(productId);
        return false;
      },
    }),
    {
      name: 'saree-wishlist',
      // Serialize Set → Array when saving to localStorage
      partialize: (state) => ({
        productIds: Array.isArray(state.productIds)
          ? state.productIds
          : [...state.productIds],
      }),
      // Deserialize Array → Set when loading from localStorage
      onRehydrateStorage: () => (state) => {
        if (state && state.productIds) {
          state.productIds = new Set(state.productIds);
        }
      },
    }
  )
);

// ─── UI Store ─────────────────────────────────────────────────────────────────
export const useUIStore = create((set) => ({
  cartOpen: false,
  searchOpen: false,
  mobileMenuOpen: false,
  quickViewProduct: null,

  openCart: () => set({ cartOpen: true }),
  closeCart: () => set({ cartOpen: false }),
  toggleCart: () => set((s) => ({ cartOpen: !s.cartOpen })),

  openSearch: () => set({ searchOpen: true }),
  closeSearch: () => set({ searchOpen: false }),

  toggleMobileMenu: () => set((s) => ({ mobileMenuOpen: !s.mobileMenuOpen })),
  closeMobileMenu: () => set({ mobileMenuOpen: false }),

  setQuickView: (product) => set({ quickViewProduct: product }),
  closeQuickView: () => set({ quickViewProduct: null }),
}));